
import React, { useState, useEffect } from "react";
import { getFileUrl } from "@/services/fileStorageService";
import { toast } from "@/hooks/use-toast";

interface PDFViewerProps {
  filePath: string | null;
  title?: string;
}

const PDFViewer: React.FC<PDFViewerProps> = ({ filePath, title }) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const loadPdf = async () => {
      if (!filePath) {
        setLoading(false);
        setError("No PDF file available for this lesson.");
        return;
      }
      
      try {
        setLoading(true);
        
        // Get public URL for the file
        const url = await getFileUrl('course-content', filePath);
        setPdfUrl(url);
        
      } catch (err) {
        console.error("Error loading PDF:", err);
        setError("Failed to load PDF file. Please try again later.");
        toast({
          title: "Error",
          description: "Could not load the PDF file.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    
    loadPdf();
  }, [filePath]);
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-[600px] bg-gray-100 rounded-md">
        <div className="text-center">
          <div className="h-8 w-8 border-4 border-t-primary border-r-primary/30 border-b-primary/30 border-l-primary/30 rounded-full animate-spin mx-auto"></div>
          <p className="mt-4">Loading PDF...</p>
        </div>
      </div>
    );
  }
  
  if (error || !pdfUrl) {
    return (
      <div className="flex justify-center items-center h-[400px] bg-gray-100 rounded-md">
        <div className="text-center max-w-md p-6">
          <h3 className="text-lg font-medium mb-2">Content Not Available</h3>
          <p>{error || "PDF file not found."}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="w-full">
      {title && <h2 className="text-xl font-bold mb-4">{title}</h2>}
      <div className="w-full aspect-[8.5/11] min-h-[600px]">
        <iframe
          src={`${pdfUrl}#view=FitH`}
          className="w-full h-full border-0 rounded-md shadow-md"
          title={title || "Lesson PDF"}
          allowFullScreen
        />
      </div>
    </div>
  );
};

export default PDFViewer;
